import {csvDecode} from '../../../supabase/functions/agentopia-skill-capability-v1/sources';
type Row=Record<string,unknown>;
const numeric=(r:Row,key:string):number|null=>{
 const raw=r[key];const normalized=typeof raw==='string'?raw.trim().toLowerCase():'';const n=normalized==='true'?1:normalized==='false'?0:typeof raw==='number'?raw:typeof raw==='string'&&raw.trim()!==''?Number(raw):NaN;
 return Number.isFinite(n)?n:null;
};
const scale=(v:number)=>Math.max(0,Math.min(100,Math.round(v*100)/100));
const score=(parts:{name:string;weight:number;score:number|null}[])=>{
 const actual=parts.filter(x=>x.score!==null);
 const total=actual.reduce((sum,x)=>sum+x.weight,0);
 if(!total)throw Error('Public source has insufficient valid indicators.');
 return {composite:scale(actual.reduce((sum,x)=>sum+x.weight*(x.score??0),0)/total),
  components:parts.map(x=>({...x,effectiveWeight:x.score===null?0:x.weight/total}))};
};
function percentile(a:number[],value:number){
 if(!a.length)return null;return scale(a.filter(x=>x<=value).length/a.length*100);
}
const zone=(n:number,labels:string[])=>labels[n>=80?0:n>=60?1:n>=40?2:n>=20?3:4];
export function scoreMarketBreadth(csv:string):Row{
 const rows=csvDecode(csv,12000);
 const valid=rows.filter(r=>numeric(r,'Breadth_Index_8MA')!==null&&
  numeric(r,'Breadth_Index_200MA')!==null&&typeof r.Date==='string')
  .sort((a,b)=>String(a.Date).localeCompare(String(b.Date)));
 if(valid.length<65)throw Error('At least 65 public breadth observations are needed.');
 const today=valid.at(-1)!,old8=valid.at(-11)!,p20=valid.at(-21)!,p60=valid.at(-61)!;
 const b8=numeric(today,'Breadth_Index_8MA')!,b200=numeric(today,'Breadth_Index_200MA')!;
 const dir=b8-(numeric(old8,'Breadth_Index_8MA')??b8);
 const gap=b8-b200;
 const history=valid.map(r=>numeric(r,'Breadth_Index_8MA')).filter((n):n is number=>n!==null);
 const pct=percentile(history,b8);
 const recent=valid.slice(-100);
 const lastSignal=[...recent].reverse().find(r=>numeric(r,'Is_Peak')===1||numeric(r,'Is_Trough')===1);
 const peak=lastSignal?numeric(lastSignal,'Is_Peak')===1:false;
 const trough=lastSignal?numeric(lastSignal,'Is_Trough')===1:false;
 const bearish=numeric(today,'Bearish_Signal');
 const divergence=(prior:Row)=>{
  const price=numeric(today,'S&P500_Price'),past=numeric(prior,'S&P500_Price');
  const pastBreadth=numeric(prior,'Breadth_Index_8MA');
  if(price===null||past===null||pastBreadth===null)return null;
  const move=price-past,participation=b8-pastBreadth;
  return move===0||participation===0?50:move*participation>0?80:20;
 };
 const d20=divergence(p20),d60=divergence(p60);
 const parts=[
  {name:'Breadth Level and Trend',weight:.25,score:scale(b8*100+dir*80)},
  {name:'8MA / 200MA Crossover',weight:.2,score:scale(50+gap*125)},
  {name:'Peak / Trough Cycle',weight:.2,score:peak?35:trough?70:scale(50+gap*50)},
  {name:'Bearish Signal',weight:.15,score:bearish===null?null:bearish===0?100:0},
  {name:'Historical Percentile',weight:.1,score:pct},
  {name:'S&P500 Divergence (20d/60d)',weight:.1,
   score:d20!==null&&d60!==null?(d20+d60)/2:d20??d60}
 ];
 const result=score(parts);
 return {...result,source:'TraderMonty Market Breadth CSV',dataDate:today.Date,
  observations:valid.length,historyIncomplete:valid.length<2000,
  zone:zone(result.composite,['Strong','Healthy','Neutral','Weakening','Critical']),
  latestBreadth8MA:b8,latestBreadth200MA:b200,
  warnings:[valid.length<2000?'Historical percentile is based on available records only.':null].filter(Boolean),
  noTradesPlaced:true};
}
export function scoreMarketUptrend(csv:string):Row{
 const all=csvDecode(csv,65000);
 const groups=new Map<string,Row[]>();
 for(const row of all){
  const key=String(row.worksheet??row.Worksheet??'').toLowerCase().trim();
  const date=String(row.date??row.Date??'');
  if(!key||!date||numeric(row,'ratio')===null)continue;
  const array=groups.get(key)||[];array.push(row);groups.set(key,array);
 }
 const market=groups.get('all')?.sort((a,b)=>String(a.date).localeCompare(String(b.date)))||[];
 if(market.length<20)throw Error('Free uptrend CSV contains insufficient market history.');
 const newest=market.at(-1)!;
 const ratio=numeric(newest,'ratio')!,old=numeric(market.at(-11)!,'ratio')??ratio;
 const recent=market.slice(-20).map(r=>numeric(r,'slope')??0);
 const slopesOld=recent.slice(0,10),slopesNew=recent.slice(10);
 const accel=(slopesNew.reduce((s,n)=>s+n,0)-slopesOld.reduce((s,n)=>s+n,0))/10;
 const marketDate=String(newest.date??newest.Date??'');
 const sectors=[...groups.entries()].filter(([k])=>k!=='all')
  .map(([name,rows])=>{
   const ordered=rows.sort((a,b)=>String(a.date??a.Date).localeCompare(String(b.date??b.Date)));
   const x=ordered.at(-1)!;
   return {sector:name,ratio:numeric(x,'ratio')??0,lastUpdated:x.date??x.Date};
  });
 const spread=sectors.length?
  Math.max(...sectors.map(s=>s.ratio))-Math.min(...sectors.map(s=>s.ratio)):null;
 const pct=percentile(market.map(r=>numeric(r,'ratio')||0),ratio);
 const parts=[
  {name:'Market Breadth',weight:.3,score:scale(ratio*100)},
  {name:'Sector Participation',weight:.25,score:sectors.length?
   scale(sectors.filter(s=>s.ratio>.5).length/sectors.length*100):null},
  {name:'Sector Rotation',weight:.15,score:spread===null?null:scale(100-spread*100)},
  {name:'Momentum',weight:.2,score:scale(50+(ratio-old)*160+accel*100)},
  {name:'Historical Context',weight:.1,score:pct}
 ];
 const r=score(parts);
 const warnings:string[]=[];
 if(spread!==null&&spread>.4)warnings.push('Wide sector participation spread');
 if(ratio<old)warnings.push('Market breadth decreased from ten observations earlier');
 return {...r,source:'TraderMonty Uptrend Dashboard CSV',dataDate:marketDate,
  observations:all.length,marketHistory:market.length,sectorCount:sectors.length,
  sectorHeatmap:sectors,latestMarketRatio:ratio,ratioTenObservationsAgo:old,
  zone:zone(r.composite,['Strong Bull','Bull','Neutral','Cautious','Bear']),
  warnings,sourceMethod:'Five weighted public indicators, score subject to data freshness',
  noTradesPlaced:true};
}
export function normalizeFreePublicJson(operation:string,raw:unknown):Row{
 const data=typeof raw==='string'?JSON.parse(raw) as Row:raw as Row;
 if(!data||typeof data!=='object')throw Error('Public JSON source response was malformed.');
 if(operation.startsWith('market_')){
  if(data.code!=='0'||!Array.isArray(data.data))throw Error('Market price source did not return a valid instrument result.');
  const first=data.data[0];
  return {operation,provider:'OKX public read-only API',observations:(data.data as unknown[]).slice(0,60),
   firstRecord:first,noOrdersPlaced:true};
 }
 if(operation==='sports_odds_live'){
  const events=Array.isArray(data.events)?data.events.slice(0,60):[];
  const scored=events.map(e=>{
   const ev=e as Row;
   const competitions=Array.isArray(ev.competitions)?ev.competitions:[];
   const comp=(competitions[0]||{}) as Row;
   const odds=Array.isArray(comp.odds)?comp.odds:[];
   const teams=Array.isArray(comp.competitors)?comp.competitors:[];
   return {id:ev.id,name:ev.name,date:ev.date,
    teams:teams.map(t=>(t as Row).team),
    publishedOdds:odds,oddsPublished:odds.length>0};
  });
  return {provider:'ESPN public scoreboard',events:scored,eventsWithPublishedOdds:scored.filter(x=>x.oddsPublished).length,
   noBetPlaced:true,note:'Missing odds remain unavailable; never synthesized.'};
 }
 if(operation==='company_search_live'){
  const q=data.query as Row|undefined;
  return {source:'Wikipedia public API',
   results:Array.isArray(q?.search)?q.search.slice(0,10):[]};
 }
 if(operation==='github_public_search')return{
  source:'GitHub public repositories',
  totalCount:data.total_count,
  results:Array.isArray(data.items)?data.items.slice(0,10).map(item=>{
   const x=item as Row;
   return {name:x.full_name,url:x.html_url,description:x.description,
    stars:x.stargazers_count,updatedAt:x.updated_at};
  }):[]
 };
 return {source:'public JSON',data};
}
